'use client';

import { FormsTemplateType } from '@commenda/types';
import { FORM_BUILDER_TEMPLATES_PAGE_SIZE } from '@commenda/utils/form-builder';
import { useCallback } from 'react';
import { useFormBuilderQueryParams } from './useFormBuilderQueryParams';

export type ActiveFilters = Record<string, string | string[]>;

export const QUERY_PARAMS = {
  OFFSET: 't_offset',
  LIMIT: 't_limit',
  SEARCH_TERM: 't_searchTerm',
  STATUS: 't_status',
  TYPE: 't_type',
  KEY: 't_key',
  HAS_LIVE_VERSION: 't_hasLiveVersion',
  DELETE_TEMPLATE_ID: 't_deleteTemplateId',
  TAB: 'tab',
} as const;

type UseFormTemplatesQueryStateReturn = {
  offset: number;
  limit: number;
  searchTerm: string;
  filters: ActiveFilters;

  // derived for tabs
  currentType: FormsTemplateType;
  currentTab: 'templates' | 'keys';

  // filters for API
  hasLiveVersionFilter?: boolean;

  // delete modal / URL state
  deleteTemplateId?: string;
  isDeleteModalOpen: boolean;

  // core operations
  setOffset: (offset: number) => void;
  setSearchTerm: (term: string) => void;
  setFilters: (filters: ActiveFilters) => void;
  setType: (type: FormsTemplateType) => void;
  setTab: (tab: 'templates' | 'keys') => void;

  openDelete: (id: string) => void;
  closeDelete: () => void;
};

export function useFormTemplatesQueryState(): UseFormTemplatesQueryStateReturn {
  const { searchParams, pushQueryParams } = useFormBuilderQueryParams();

  // ----- basic query params -----
  const offsetParam = searchParams.get(QUERY_PARAMS.OFFSET);
  let offset = 0;
  if (offsetParam) {
    const parsed = Number.parseInt(offsetParam, 10);
    offset = Number.isNaN(parsed) ? 0 : parsed;
  }

  const limitParam = searchParams.get(QUERY_PARAMS.LIMIT);
  let limit = FORM_BUILDER_TEMPLATES_PAGE_SIZE;
  if (limitParam) {
    const parsed = Number.parseInt(limitParam, 10);
    limit = Number.isNaN(parsed) ? FORM_BUILDER_TEMPLATES_PAGE_SIZE : parsed;
  }

  const searchTerm = searchParams.get(QUERY_PARAMS.SEARCH_TERM) || '';

  const deleteTemplateId =
    searchParams.get(QUERY_PARAMS.DELETE_TEMPLATE_ID) || undefined;

  // ----- filters parsing -----
  const filters: ActiveFilters = {};

  const status = searchParams.getAll(QUERY_PARAMS.STATUS);
  if (status.length > 0) {
    filters[QUERY_PARAMS.STATUS] = status; // Always array for MULTI_SELECT
  }

  // type is stored as a single comma-separated query param (?type=a,b,c)
  const typeParam = searchParams.get(QUERY_PARAMS.TYPE);
  if (typeParam) {
    const typeParts = typeParam
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    filters[QUERY_PARAMS.TYPE] = typeParts; // Always array for MULTI_SELECT
  } else {
    filters[QUERY_PARAMS.TYPE] = [FormsTemplateType.FORM_TEMPLATE];
  }

  // key is stored as a comma-separated string (?key=foo,bar)
  const keyParam = searchParams.get(QUERY_PARAMS.KEY);
  if (keyParam) {
    const keyParts = keyParam
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);
    if (keyParts.length > 0) {
      filters[QUERY_PARAMS.KEY] = keyParts; // Always array for MULTI_SELECT
    }
  }

  // hasLiveVersion filter
  const hasLiveVersionRaw = searchParams.get(QUERY_PARAMS.HAS_LIVE_VERSION);
  if (hasLiveVersionRaw === 'Yes' || hasLiveVersionRaw === 'No') {
    filters[QUERY_PARAMS.HAS_LIVE_VERSION] = hasLiveVersionRaw;
  }

  // hasLiveVersion mapped to API type
  let hasLiveVersionFilter: boolean | undefined;
  if (filters[QUERY_PARAMS.HAS_LIVE_VERSION] === 'Yes')
    hasLiveVersionFilter = true;
  else if (filters[QUERY_PARAMS.HAS_LIVE_VERSION] === 'No')
    hasLiveVersionFilter = false;

  // ----- derived: currentType for tabs -----
  const typeFilter = filters[QUERY_PARAMS.TYPE];
  const typeArray = typeFilter
    ? Array.isArray(typeFilter)
      ? typeFilter
      : [typeFilter]
    : [FormsTemplateType.FORM_TEMPLATE];

  const currentType =
    (typeArray[0] as FormsTemplateType) || FormsTemplateType.FORM_TEMPLATE;

  // ----- derived: currentTab -----
  // Read from searchParams, with fallback to window.location for hydration edge cases
  const getTabFromUrl = (): 'templates' | 'keys' => {
    const tabParam = searchParams.get(QUERY_PARAMS.TAB);
    if (tabParam === 'templates') return 'templates';
    if (tabParam === 'keys') return 'keys';

    // Fallback: read directly from window.location during hydration
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const tabFromWindow = urlParams.get(QUERY_PARAMS.TAB);
      if (tabFromWindow === 'keys') return 'keys';
    }

    return 'templates';
  };

  const currentTab = getTabFromUrl();

  // ----- helpers -----
  const updateQueryParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      // Use window.location.search as source of truth to avoid losing params during hydration
      const params = new URLSearchParams(
        typeof window !== 'undefined'
          ? window.location.search
          : searchParams.toString()
      );
      mutate(params);
      pushQueryParams(params);
    },
    [pushQueryParams, searchParams]
  );

  const setOffset = useCallback(
    (newOffset: number) => {
      updateQueryParams((params) => {
        params.set(QUERY_PARAMS.OFFSET, String(newOffset));
      });
    },
    [updateQueryParams]
  );

  const setSearchTerm = useCallback(
    (term: string) => {
      updateQueryParams((params) => {
        if (term) {
          params.set(QUERY_PARAMS.SEARCH_TERM, term);
        } else {
          params.delete(QUERY_PARAMS.SEARCH_TERM);
        }
        params.set(QUERY_PARAMS.OFFSET, '0'); // reset page
      });
    },
    [updateQueryParams]
  );

  const setFilters = useCallback(
    (newFilters: ActiveFilters) => {
      updateQueryParams((params) => {
        params.set(QUERY_PARAMS.OFFSET, '0');

        params.delete(QUERY_PARAMS.STATUS);
        const statusVal = newFilters[QUERY_PARAMS.STATUS];
        if (statusVal) {
          const statusArr = Array.isArray(statusVal) ? statusVal : [statusVal];
          statusArr.forEach((s) =>
            params.append(QUERY_PARAMS.STATUS, String(s))
          );
        }

        const typeVal = newFilters[QUERY_PARAMS.TYPE];
        if (typeVal) {
          const typeArr = Array.isArray(typeVal) ? typeVal : [typeVal];
          params.set(QUERY_PARAMS.TYPE, typeArr.join(','));
        } else {
          params.delete(QUERY_PARAMS.TYPE);
        }

        const keyVal = newFilters[QUERY_PARAMS.KEY];
        if (keyVal) {
          const keyArr = Array.isArray(keyVal) ? keyVal : [keyVal];
          params.set(QUERY_PARAMS.KEY, keyArr.join(','));
        } else {
          params.delete(QUERY_PARAMS.KEY);
        }
      });
    },
    [updateQueryParams]
  );

  const setType = useCallback(
    (type: FormsTemplateType) => {
      updateQueryParams((params) => {
        params.set(QUERY_PARAMS.TYPE, type);
        params.set(QUERY_PARAMS.OFFSET, '0');
        params.delete(QUERY_PARAMS.SEARCH_TERM);
        params.set(QUERY_PARAMS.TAB, 'templates'); // Switch to templates view
      });
    },
    [updateQueryParams]
  );

  const setTab = useCallback(
    (tab: 'templates' | 'keys') => {
      updateQueryParams((params) => {
        params.set(QUERY_PARAMS.TAB, tab);
        params.set(QUERY_PARAMS.OFFSET, '0');
        params.delete(QUERY_PARAMS.SEARCH_TERM);
      });
    },
    [updateQueryParams]
  );

  const openDelete = useCallback(
    (id: string) => {
      updateQueryParams((params) => {
        params.set(QUERY_PARAMS.DELETE_TEMPLATE_ID, id);
      });
    },
    [updateQueryParams]
  );

  const closeDelete = useCallback(() => {
    updateQueryParams((params) => {
      params.delete(QUERY_PARAMS.DELETE_TEMPLATE_ID);
    });
  }, [updateQueryParams]);

  const isDeleteModalOpen = Boolean(deleteTemplateId);

  return {
    offset,
    limit,
    searchTerm,
    filters,
    currentType,
    currentTab,
    deleteTemplateId,
    isDeleteModalOpen,
    setOffset,
    setSearchTerm,
    setFilters,
    setType,
    setTab,
    openDelete,
    closeDelete,
  };
}
